import axios from "axios";
import { defineStore } from "pinia";
import type {
    ApiVehicleDetails, RcaVehicleForm,
    VehicleCategory,
    VehicleFuelType,
    VehicleMake,
    VehicleModel,
    VehicleType,
    VehicleUsageType
} from "~/types/Vehicle";
import type {RcaCustomerForm} from "~/types/Customer";
export const useVehicleStore = defineStore('vehicle', {
    state: () => ({
        vehicleTypes: [] as VehicleType[],
        vehicleCategories: [] as VehicleCategory[],
        vehicleMakes: [] as VehicleMake[],
        vehicleModels: [] as VehicleModel[],
        vehicleUsageTypes: [] as VehicleUsageType[],
        _fetchVehicleTypesPromise: null as Promise<void> | null,
        _fetchVehicleCategoriesPromise: null as Promise<void> | null,
        _fetchVehicleMakesPromise: null as Promise<void> | null,
        _fetchVehicleModelsPromise: null as Promise<void> | null,
        _fetchVehicleUsageTypesPromise: null as Promise<void> | null,
        vehicleFuelTypes: [] as VehicleFuelType[],
        _fetchVehicleFuelTypesPromise: null as Promise<void> | null,
        vehicle: null as ApiVehicleDetails | null,
        _storeVehiclePromise: null as Promise<any> | null,
        vehicles: [] as ApiVehicleDetails[],
        _fetchVehiclesPromise: null as Promise<void> | null,
        vehicleCategoryTow: <VehicleCategory> {
            id: 6,
            name: "Remorca / Semiremorca / Rulota",
            ws_id: 8,
            code: "trailer",
        },
        vehicleFuelTypeElectric: <VehicleFuelType> {
            id: 3,
            code: "electric",
            name: "Electric"
        },
        vehicleFuelTypeWithout: <VehicleFuelType> {
            id: 6,
            code: "without",
            name: "Fara"
        },
    }),
    actions: {
        async fetchVehicleTypes() {
            if (this._fetchVehicleTypesPromise) return this._fetchVehicleTypesPromise;

            this._fetchVehicleTypesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles/types`);
                    if (response.data.data) {
                        this.vehicleTypes = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleTypesPromise = null;
                }
            })();

            return this._fetchVehicleTypesPromise;
        },
        async fetchVehicleCategories() {
            if (this._fetchVehicleCategoriesPromise) return this._fetchVehicleCategoriesPromise;

            this._fetchVehicleCategoriesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles/categories`);
                    if (response.data.data) {
                        this.vehicleCategories = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleCategoriesPromise = null;
                }
            })();

            return this._fetchVehicleCategoriesPromise;
        },
        async fetchVehicleMakes(vehicleCategoryWsId: number) {
            if (this._fetchVehicleMakesPromise) return this._fetchVehicleMakesPromise;

            this._fetchVehicleMakesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-vehicle-make/` + vehicleCategoryWsId);
                    if (response.data.data) {
                        this.vehicleMakes = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleMakesPromise = null;
                }
            })();

            return this._fetchVehicleMakesPromise;
        },
        async fetchVehicleModels(vehicleMakeId: number) {
            if (this._fetchVehicleModelsPromise) return this._fetchVehicleModelsPromise;

            this._fetchVehicleModelsPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/rca-vehicle-models/` + vehicleMakeId);
                    if (response.data.data) {
                        this.vehicleModels = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleModelsPromise = null;
                }
            })();

            return this._fetchVehicleModelsPromise;
        },
        async fetchVehicleUsageTypes() {
            if (this._fetchVehicleUsageTypesPromise) return this._fetchVehicleUsageTypesPromise;

            this._fetchVehicleUsageTypesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicle-usage-types`);
                    if (response.data.data) {
                        this.vehicleUsageTypes = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleUsageTypesPromise = null;
                }
            })();

            return this._fetchVehicleUsageTypesPromise;
        },
        async getVehicleUsageTypes(): Promise<VehicleUsageType[]> {
            if (this.vehicleUsageTypes.length === 0) {
                await this.fetchVehicleUsageTypes();
            }
            return this.vehicleUsageTypes;
        },
        async getVehicleTypes() {
            if (this.vehicleTypes.length === 0) {
                await this.fetchVehicleTypes();
            }
            return this.vehicleTypes;
        },
        async getVehicleCategories() {
            if (this.vehicleCategories.length === 0) {
                await this.fetchVehicleCategories();
            }
            return this.vehicleCategories;
        },
        async getVehicleMakes(vehicleCategoryWsId: number) {
            await this.fetchVehicleMakes(vehicleCategoryWsId);
            return this.vehicleMakes;
        },
        async getVehicleModels(vehicleMakeId: number) {
            await this.fetchVehicleModels(vehicleMakeId);
            return this.vehicleModels;
        },
        async fetchVehicleFuelTypes() {
            if (this._fetchVehicleFuelTypesPromise) return this._fetchVehicleFuelTypesPromise;

            this._fetchVehicleFuelTypesPromise = (async () => {
                try {
                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicle-fuel-types`);
                    if (response.data.data) {
                        this.vehicleFuelTypes = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehicleFuelTypesPromise = null;
                }
            })();

            return this._fetchVehicleFuelTypesPromise;
        },
        async getVehicleFuelTypes() {
            if (this.vehicleFuelTypes.length === 0) {
                await this.fetchVehicleFuelTypes();
            }
            return this.vehicleFuelTypes;
        },
        async fetchVehicleDetails(registrationPlate: string|null, vin: string|null, accessToken: string|null, token: string|undefined, vehicleId?: number|null): Promise<ApiVehicleDetails|null> {
            try {
                const config = accessToken ? {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                } : {};

                const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles/data`, {
                    registration_plate: registrationPlate,
                    vin: vin,
                    token: token,
                    id: vehicleId,
                }, config);

                return response.data.data;
            } catch (error) {
                return null;
            }
        },
        async fetchVehicles(accessToken: string) {
            if (this._fetchVehiclesPromise) return this._fetchVehiclesPromise;

            this._fetchVehiclesPromise = (async () => {
                try {
                    const config = {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    };

                    const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles`, config);
                    if (response.data.data) {
                        this.vehicles = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._fetchVehiclesPromise = null;
                }
            })();

            return this._fetchVehiclesPromise;
        },
        async getVehicles(accessToken: string): Promise<ApiVehicleDetails[]> {
            if (this.vehicles.length === 0) {
                await this.fetchVehicles(accessToken);
            }
            return this.vehicles;
        },
        async fetchVehicle(vehicleId: number, accessToken: string) {
            try {
                const config = {
                    headers: {
                        Authorization: `Bearer ${accessToken}`
                    }
                };

                const response = await axios.get(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles/${vehicleId}`, config);

                return response.data.data;
            } catch (error) {
                return null;
            }
        },
        async getVehicle(vehicleId: number, accessToken: string): Promise<ApiVehicleDetails|null> {
            return await this.fetchVehicle(vehicleId, accessToken);
        },
        async storeVehicle(vehicleForm: RcaVehicleForm, customerForm: RcaCustomerForm, accessToken: string|null, token: string|undefined) {
            if (this._storeVehiclePromise) return this._storeVehiclePromise;

            this._storeVehiclePromise = (async () => {
                try {
                    const config = accessToken ? {
                        headers: {
                            Authorization: `Bearer ${accessToken}`
                        }
                    } : {};

                    const response = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/api/v1/vehicles`, {
                        vehicle: vehicleForm,
                        customer: customerForm,
                        token,
                    }, config);
                    if (response.data.data) {
                        this.vehicle = response.data.data;
                    }
                } catch (error) {
                    throw error;
                } finally {
                    this._storeVehiclePromise = null;
                }
            })();

            return this._storeVehiclePromise;
        },
    },
    persist: {
        storage: persistedState.sessionStorage,
        paths: [
            'vehicleTypes',
            'vehicleCategories',
            'vehicleUsageTypes',
            'vehicleFuelTypes'
        ],
        key: 'vehicleStore',
    }
});
